import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { AccountService } from '@core/services/account.service';
import { ApiResponse } from '@core/types';
import { Account, AccountDetail, AccountCreate, AccountUpdate } from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class AccountQuery {
  private readonly accountService = inject(AccountService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  useAccounts(options?: { enabled?: () => boolean }) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const extraEnabled = options?.enabled ? options.enabled() : true;
      return {
        queryKey: [...QUERY_KEYS.accounts.list(), condominiumId],
        queryFn: () => lastValueFrom(this.accountService.getAccounts()),
        select: (data: ApiResponse<Account[]>) => data.data || [],
        staleTime: environment.staleTime,
        enabled: !!condominiumId && extraEnabled,
      };
    });
  }

  useAccountDetail(accountId: () => string | null) {
    return injectQuery(() => {
      const id = accountId();
      return {
        queryKey: QUERY_KEYS.accounts.detail(id || ''),
        queryFn: () => lastValueFrom(this.accountService.getAccount(id!)),
        select: (data: ApiResponse<AccountDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useCreateAccount() {
    return injectMutation(() => ({
      mutationFn: (data: AccountCreate) =>
        lastValueFrom(this.accountService.createAccount(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useUpdateAccount() {
    return injectMutation(() => ({
      mutationFn: (data: AccountUpdate) =>
        lastValueFrom(this.accountService.updateAccount(data)),
      onSuccess: (_result, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
      },
    }));
  }

  useDeleteAccount() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.accountService.deleteAccount(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }
}
